import { DomSanitizer } from '@angular/platform-browser';
import { Injectable } from '@angular/core';
import { Base } from "./Classes/base";
import { Observable, Subject } from 'rxjs';

import {
    // Unit factory dependencies
    ParkingSpaceFactory, WarehouseFactory, UnitPhotoFactory,
    UnitFactory,
    // Investor factories
    InvestorAddressFactory, InvestorContactFactory, InvestorFileFactory, InvestorFactory,

} from './Factories';
import {
    InvestorService,
    InvestorCustomActionsService,
    InvestorFilesService,
    ConfigService,
    SummaryService,
    PaginationService
} from '../_services';
import { Investor as T, Serialized, InvestorAddress, InvestorContact } from './Interfaces';
import { Investor as C, Page, InvestorFile } from './Classes';
import { environment } from '../../environments/environment';
export const INVESTOR_INCLUDES = 'bank_data,fiscal_address,address';

@Injectable()
export class Investor extends Base {
    item: C;
    banks: any;
    collection: C[] = [];
    changed$: Subject<C> = new Subject<C>();
    file: InvestorFile;
    page: Page = new Page();
    sanitizer: DomSanitizer;
    constructor(
        private naf: InvestorAddressFactory,
        private cf: InvestorContactFactory,
        private ff: InvestorFileFactory,
        private factory: InvestorFactory,
        private main: InvestorService,
        private custom: InvestorCustomActionsService,
        private fs: InvestorFilesService,
        private cs: ConfigService,
        private ss: SummaryService,
        private paging: PaginationService
    ) {
        super();
        if (!this.item) this.make({});

    }
    empty() {
        return !this.item.id;
    }
    get instance() {
        return this.item;
    }
    set instance(value) {
        this.item = value;
        this.changed$.next(this.item);
    }
    isInstance(test) {
        return test instanceof C;
    }
    make(json) {
        this.item = this.factory.createInstance(json);
        this.changed$.next(this.item);
        return this.item;
    }
    get(id: number, params?: string): Observable<C> {
        return this.main.get(id, params).map((response: Serialized) => {
            this.item = this.factory.createInstance(response.data);
            this.changed$.next(this.item);
            return this.item;
        });
    }
    getAll(params?: string): Observable<C[]> {
        return this.main.getAll(params).map((response: Serialized) => {
            this.assignPageAndCollection(
                response.meta.pagination, response.data);
            return this.collection;
        });
    }
    getAllWUnits(params?: string): Observable<C[]> {
        return this.custom.getAll(params).map((response: Serialized) => {
            this.assignPageAndCollection(
                response.meta.pagination, response.data);
            return this.collection;
        });
    }
    post(data: (T | any)) {
        return this.main.post(data).map((response: Serialized) => {
            this.item = this.factory.createInstance(response.data);
            this.changed$.next(this.item);
            return this.item;
        });
    }
    put(id: number, data: (T | any)) {
        return this.main.put(id, data).map((response: Serialized) => {
            return response;
        });
    }
    search(searchString: string) {
        return this.custom.search(searchString).map((response: Serialized) => {
            this.assignPageAndCollection(
                response.meta.pagination, response.data);
            return this.collection;
        });
    }
    getAddress(id: number) {
        return this.custom.getAddress(id).map((response: Serialized) => {
            this.item.address = this.naf.createInstance(response.data);
            return this.item.address;
        });
    }
    copyFiscalAddress(id: number) {
        return this.custom.copyFiscalAddress(id).map((response: Serialized) => {
            this.item.address = this.naf.createInstance(response.data);
            return this.item.address;
        })
    }
    postAddress(id: number, data: (InvestorAddress | any)) {
        return this.custom.postAddress(id, data).map((response: Serialized) => {
            return response;
        });
    }
    getContacts(id: number, params?: string) {
        return this.custom.getContacts(id, params).map((response: Serialized) => {
            this.item.contacts = this.cf.createInstances(response.data);
            return this.item.contacts;
        });
    }

    postContact(id: number, data: (InvestorContact | any)) {
        return this.custom.postContact(id, data).map((response: Serialized) => {
            return response;
        })
    }
    getContact(id: number, contactId: number) {
        return this.custom.getContact(id, contactId).map((response: Serialized) => {
            return this.cf.createInstance(response.data);
        })
    }
    deleteContact(investorId, contactId) {
        return this.custom.deleteContact(investorId, contactId).map((response: Serialized) => {
            return response;
        });
    }
    updateContact(investorId, contactId, body) {
        return this.custom.updateContact(investorId, contactId, body).map((response: Serialized) => {
            return response;
        });
    }
    getFiles(id: number, params?: string) {
        return this.fs.getAll(id, params).map((response: Serialized) => {
            let url = `${environment.fileEndpoint}/`;
            response.data.map(file => { file.data = url; });
            this.item.files =
                this.ff.createInstances(response.data).map(file => {
                    file = this.trustFile(file);
                    return file;
                })
            return this.item.files;
        });
    }
    trustFile(file) {
        if (file.isImage())
            file.data = this.sanitizer.bypassSecurityTrustUrl(file.data);
        else {
            file.data = this.sanitizer.bypassSecurityTrustResourceUrl(file.data);
        }
        return file;
    }
    getSummary(params?: string) {
        return this.ss.getSummary().map((response: Serialized) => {
            return response.data;
        });
    }
    deleteFile(investorId: number, fileId: number) {
        return this.fs.delete(investorId, fileId).map((response: Serialized) => {
            return response;
        });
    }
    findWithAttr(array, attr, value) {
        for (var i = 0; i < array.length; i += 1) {
            if (array[i][attr] === value) {
                return i;
            }
        }
        return -1;
    }
    getDocumentsToAsk(params?: string) {
        return this.cs.getAllInvestorDocuments(params).map((response: Serialized) => {
            return response.data;
        });
    }
    getBanks(params?: string): Observable<object> {
        return this.main.getBanks(params).map((response: Serialized) => {
            return response;
        });
    }
    setPage(page, params?: string) {
        let goingPage = page.offset + 1;
        let go: any;
        if (this.page.current_page < goingPage && this.withinPageLimit(page.offset))
            go = this.nextPage(goingPage, params);
        if (this.page.current_page > goingPage && this.withinPageLimit(page.offset))
            go = this.prevPage(goingPage, params);
        if (go) {
            this.page.current_page = goingPage;
            go.subscribe(response => { });
        }
    }
    nextPage(next: number, params?: string) {
        return this.paging.next(this.page, params).map((response: Serialized) => {
            this.assignPageAndCollection(
                response.meta.pagination, response.data);
            return response;
        });
    }
    prevPage(prev: number, params?: string) {
        return this.paging.prev(this.page, params).map((response: Serialized) => {
            this.assignPageAndCollection(
                response.meta.pagination, response.data);
            return response;
        });
    }
    withinPageLimit(next: number) {
        return next < this.page.total_pages;
    }

    private assignPageAndCollection(page: Page, collection: (T | any)[]) {
        this.collection = this.factory.createInstances(collection);
        this.page = new Page(page);
    }
}

export const InvestorProviders = [
    // UnitFactory dependencies
    ParkingSpaceFactory,
    WarehouseFactory,
    UnitPhotoFactory,

    // Http services
    InvestorService,
    InvestorCustomActionsService,
    InvestorFilesService,
    // Config Service
    ConfigService,
    // Summary
    SummaryService,
    // Pagination
    PaginationService,
    {
        provide: UnitFactory,
        useClass: UnitFactory,
        deps: [ParkingSpaceFactory, WarehouseFactory, UnitPhotoFactory]
    },
    InvestorAddressFactory,
    InvestorContactFactory,
    InvestorFileFactory,
    {
        provide: InvestorFactory,
        useClass: InvestorFactory,
        deps: [UnitFactory]
    },
    // This class dependencies
    {
        provide: Investor,
        useClass: Investor,
        deps: [
            InvestorAddressFactory, InvestorContactFactory, InvestorFileFactory,
            InvestorFactory, InvestorService, InvestorCustomActionsService,
            InvestorFilesService, ConfigService, SummaryService, PaginationService
        ]
    }
];



// WEBPACK FOOTER //
// /Users/cmedina/Code/motto_frontend/src/app/_models/investor.ts